import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  MapPin,
  Loader2,
  AlertCircle,
  Star,
  Calendar,
  User,
  ChevronLeft,
  ChevronRight,
  Heart,
} from "lucide-react";
import { Button } from "@tabipla/ui/components/button";
import {
  usePublicBooklet,
  useLikeBooklet,
  useUnlikeBooklet,
} from "../../application/hooks/useDiscover";
import { useAuth } from "../../application/hooks/useAuth";
import { getCategoryById } from "../../config/categories";
import { LikeUsersModal } from "../components/discover/LikeUsersModal";
import { getCategoryGradient, COVER_GRADIENT } from "../../utils/style-config";
import type { MekkeBookletSpotDto } from "@tabipla/shared";

export function PublicBookletDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: booklet, isLoading, error } = usePublicBooklet(id);
  const { user } = useAuth();
  const likeBooklet = useLikeBooklet();
  const unlikeBooklet = useUnlikeBooklet();
  const [likeUsersModalOpen, setLikeUsersModalOpen] = useState(false);

  const handleLikeToggle = () => {
    if (!id || !user) return;
    if (booklet?.liked) {
      unlikeBooklet.mutate(id);
    } else {
      likeBooklet.mutate(id);
    }
  };

  const isLiking = likeBooklet.isPending || unlikeBooklet.isPending;
  const isOwnBooklet = user?.id === booklet?.author.id;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-white via-gray-50 to-indigo-50/50 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/30">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error || !booklet) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-white via-gray-50 to-indigo-50/50 px-4 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/30">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
          <AlertCircle className="h-10 w-10 text-destructive" />
        </div>
        <h2 className="mb-2 text-lg font-semibold">{t("shared.notFound")}</h2>
        <p className="mb-6 text-center text-sm text-muted-foreground">
          {t("shared.notFoundDescription")}
        </p>
        <Link to="/discover">
          <Button className="bg-indigo-500 text-white hover:bg-indigo-600">
            {t("discover.backToDiscover")}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-indigo-50/50 pb-8 dark:from-gray-950 dark:via-gray-900 dark:to-indigo-950/30">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex max-w-4xl items-center px-4 py-3">
          <Link
            to="/discover"
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted"
          >
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div className="flex flex-1 items-center justify-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500">
              <MapPin className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold text-indigo-600">{t("app.name")}</span>
          </div>
          <div className="w-8" /> {/* Spacer for balance */}
        </div>
      </header>

      <div className="mx-auto max-w-4xl">
        {/* Cover Image */}
        <div className="relative h-48 overflow-hidden sm:h-64">
          {booklet.coverImageUrl ? (
            <img
              src={booklet.coverImageUrl}
              alt={booklet.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="relative h-full w-full" style={{ background: COVER_GRADIENT.gradient }}>
              {/* Decorative circles */}
              <div
                className="absolute -right-8 -top-8 h-32 w-32 rounded-full"
                style={{ backgroundColor: COVER_GRADIENT.decor1 }}
              />
              <div
                className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full"
                style={{ backgroundColor: COVER_GRADIENT.decor2 }}
              />
              <div
                className="absolute bottom-12 right-12 h-16 w-16 rounded-full"
                style={{ backgroundColor: COVER_GRADIENT.decor3 }}
              />
              {/* Spot count indicator */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex flex-col items-center gap-0.5 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                  <span className="text-5xl font-extrabold">{booklet.spotCount}</span>
                  <span className="text-sm font-bold uppercase tracking-widest">spots</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-4 pt-6">
          {/* Author Link */}
          <Link
            to={`/users/${booklet.author.id}`}
            className="group mb-4 inline-flex items-center gap-3 rounded-xl border border-transparent bg-muted/30 px-3 py-2 transition-all hover:border-primary/30 hover:bg-primary/10 hover:shadow-sm active:scale-[0.98]"
          >
            {booklet.author.image ? (
              <img
                src={booklet.author.image}
                alt={booklet.author.name}
                className="h-10 w-10 rounded-full object-cover ring-2 ring-muted-foreground/20 transition-all group-hover:ring-primary"
              />
            ) : booklet.author.avatarEmoji && booklet.author.avatarColor ? (
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br ${booklet.author.avatarColor} text-lg ring-2 ring-primary/30 transition-all group-hover:ring-primary`}
              >
                {booklet.author.avatarEmoji}
              </div>
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 ring-2 ring-primary/30 transition-all group-hover:ring-primary">
                <User className="h-5 w-5 text-primary" />
              </div>
            )}
            <div className="flex flex-col">
              <span className="font-medium transition-colors group-hover:text-primary">
                {booklet.author.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {t("users.viewProfile", "プロフィールを見る")}
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-primary" />
          </Link>

          {/* Title */}
          <h1 className="text-2xl font-bold">{booklet.title}</h1>
          {booklet.description && (
            <p className="mt-2 text-muted-foreground">{booklet.description}</p>
          )}

          {/* Meta Info */}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {t("booklets.spots", { count: booklet.spotCount })}
            </span>
            {booklet.createdAt && (
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(booklet.createdAt).toLocaleDateString("ja-JP")}
              </span>
            )}
          </div>

          {/* Like Section */}
          <div className="mt-4 flex items-center gap-3">
            {/* Like Button */}
            {user && !isOwnBooklet && (
              <Button
                variant={booklet.liked ? "default" : "outline"}
                size="sm"
                onClick={handleLikeToggle}
                disabled={isLiking}
                className={
                  booklet.liked
                    ? "bg-rose-500 text-white hover:bg-rose-600"
                    : "border-rose-200 text-rose-500 hover:bg-rose-50 hover:text-rose-600"
                }
              >
                <Heart className={`mr-1.5 h-4 w-4 ${booklet.liked ? "fill-current" : ""}`} />
                {booklet.liked ? t("booklets.liked", "いいね済み") : t("booklets.like", "いいね")}
              </Button>
            )}

            {/* Like Count (clickable to show users) */}
            {(booklet.likeCount ?? 0) > 0 && (
              <button
                onClick={() => setLikeUsersModalOpen(true)}
                className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-rose-500"
              >
                <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
                <span>
                  {t("booklets.likeCount", "{{count}}人がいいね", {
                    count: booklet.likeCount,
                  })}
                </span>
              </button>
            )}
          </div>

          {/* Spots List */}
          <div className="mt-8">
            <h2 className="mb-4 font-semibold">{t("nav.spots")}</h2>

            {booklet.spots && booklet.spots.length > 0 ? (
              <div className="space-y-3">
                {booklet.spots.map((bookletSpot: MekkeBookletSpotDto) => {
                  const spot = bookletSpot.spot;
                  const category = getCategoryById(spot.category);
                  const CategoryIcon = category.icon;

                  return (
                    <Link
                      key={bookletSpot.id}
                      to={`/booklets/public/${booklet.id}/${spot.id}`}
                      className="flex items-center gap-3 rounded-xl border bg-card p-3 shadow-sm transition-all hover:shadow-md active:scale-[0.99]"
                    >
                      {spot.images && spot.images.length > 0 ? (
                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg">
                          <img
                            src={spot.images[0].url}
                            alt={spot.name}
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                        <div
                          className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg"
                          style={{
                            background: getCategoryGradient(spot.category).gradient,
                          }}
                        >
                          {/* Decorative circle */}
                          <div
                            className="absolute -right-2 -top-2 h-6 w-6 rounded-full"
                            style={{
                              backgroundColor: getCategoryGradient(spot.category).decor1,
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <CategoryIcon className="h-6 w-6 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]" />
                          </div>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium">{spot.name}</h3>
                        {spot.rating && (
                          <div className="mt-0.5 flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-3 w-3 ${
                                  i < spot.rating!
                                    ? "fill-amber-400 text-amber-400"
                                    : "text-muted-foreground/30"
                                }`}
                              />
                            ))}
                          </div>
                        )}
                        {spot.address && (
                          <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">
                            {spot.address}
                          </p>
                        )}
                        {bookletSpot.comment && (
                          <p className="mt-1 text-sm italic text-muted-foreground">
                            "{bookletSpot.comment}"
                          </p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <p className="text-sm text-muted-foreground">{t("booklets.noSpots")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Like Users Modal */}
      {id && (
        <LikeUsersModal
          bookletId={id}
          open={likeUsersModalOpen}
          onOpenChange={setLikeUsersModalOpen}
        />
      )}
    </div>
  );
}
